import { inject, Injectable, Signal } from "@angular/core";
import { ArticleInfos } from "../../models/article.model";
import { ArticlesInfosApiService } from "./article-infos-api.service";
import {
  ArticleInfosState,
  ArticlesInfosStoreService,
} from "./article-infos-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { firstValueFrom } from "rxjs";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { StatsArticleDialogComponent } from "../../containers/stats-article-dialog/stats-article-dialog.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";

@Injectable({ providedIn: "root" })
export class ArticlesInfosFacadeService extends EntityFacadeService<
  ArticleInfos,
  ArticlesInfosApiService,
  ArticleInfosState,
  EntityMetadata,
  ArticlesInfosStoreService
> {
  dialogs = inject(DialogsService);

  constructor() {
    const api = inject(ArticlesInfosApiService);
    const store = inject(ArticlesInfosStoreService);
    super(api, store);
    this.store.initStatus(ArticlesInfosFacadeService);
  }

  async updateNotCurrent(articleInfos: ArticleInfos) {
    const saved = await firstValueFrom(this.api.update(articleInfos));
    return saved;
  }

  getByArticleId(articleId: string): Signal<ArticleInfos> {
    return this.store.getByArticleId(articleId);
  }

  getByCode(code: string): Signal<ArticleInfos> {
    return this.store.getByCode(code);
  }

  getByEAN14(ean14: string): Signal<ArticleInfos> {
    return this.store.getByEAN14(ean14);
  }

  @Untrack
  @UpdateStatus
  async getEan13Dispo() {
    const ean13 = await this.api.getEan13Dispo();
    return ean13;
  }

  onStats(articleId: string) {
    this.dialogs.open(StatsArticleDialogComponent, "95dvw", "95dvh", {
      articleId,
    });
  }

  @Untrack
  @UpdateStatus
  async loadByEan13(ean13: string) {
    return await firstValueFrom(this.api.loadByEan13(ean13));
  }

  @Untrack
  @UpdateStatus
  async loadByEan14(ean14: string) {
    return await firstValueFrom(this.api.loadByEan14(ean14));
  }

  @Untrack
  @UpdateStatus
  async loadByCode(code: string) {
    return await firstValueFrom(this.api.loadByCode(code));
  }

  @Untrack
  @UpdateStatus
  async loadByArticleId(articleId: string) {
    const res = await firstValueFrom(this.api.loadByArticleId(articleId));
    if (res) {
      this.store.upsertOne({ ...res, articleId });
    }
    return res;
  }

  @Untrack
  @UpdateStatus
  async loadByNumeroLot(numero: string) {
    return await firstValueFrom(this.api.loadByNumeroLot(numero));
  }

  @Untrack
  @UpdateStatus
  async updateCurrentArticleInfos(
    articleId: string,
    ean13: string,
    ean14: string,
  ) {
    const res = await firstValueFrom(
      this.api.updateCurrentArticleInfos(articleId, ean13, ean14),
    );

    this.store.upsertOne({ ...res, articleId });
    return { ...res, articleId };
  }
}
